<template>
  <div class="episode-item">
    <div class="episode-item__thumb">
      <img
        v-if="episode && hasEpisodeImage"
        :src="episodeImage"
        class="episode-image"
        alt="episode image"
      >
      <div v-else-if="!episode" class="loading-placeholder square"/>
    </div>
    <div class="episode-item__details">
      <div class="episode-item__access-text">
        <div v-if="episode">{{ accessText }}</div>
        <div
          v-else
          class="loading-placeholder string"
          style="width: 50%;"
        />
      </div>
      <div class="episode-item__name">
        <div v-if="episode">
          <router-link :to="episodeSlugLink">{{ episodeName }}</router-link>
        </div>
        <div v-else class="loading-placeholder string"/>
      </div>
      <div class="episode-item__description">
        <div v-if="episode" v-html="episodeDetails"/>
        <div
          v-else
          class="loading-placeholder string"
          style="width: 80%;"
        />
      </div>
    </div>
    <div class="episode-item__actions">
      <audio-icon
        v-if="isListenAvailable"
        :episode="episode"
        :episode-legacy="formatLegacyEpisodeObject(episode)"
      />
      <div v-else-if="episode" class="episode-item__action-locked">
        <svg svg-inline="" width="24" @click="toSubscribe" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 45 45"><circle cx="22.5" cy="22.5" r="21.75" stroke="#00AECB" stroke-opacity="0.5" stroke-width="1.5"/><path fill="#4BABC7" d="M31.125 16.3333H29.6875V13.6667C29.6875 9.98667 26.4675 7 22.5 7C18.5325 7 15.3125 9.98667 15.3125 13.6667V16.3333H13.875C12.2937 16.3333 11 17.5333 11 19V32.3333C11 33.8 12.2937 35 13.875 35H31.125C32.7062 35 34 33.8 34 32.3333V19C34 17.5333 32.7062 16.3333 31.125 16.3333ZM22.5 28.3333C20.9187 28.3333 19.625 27.1333 19.625 25.6667C19.625 24.2 20.9187 23 22.5 23C24.0813 23 25.375 24.2 25.375 25.6667C25.375 27.1333 24.0813 28.3333 22.5 28.3333ZM26.9563 16.3333H18.0437V13.6667C18.0437 11.3867 20.0419 9.53333 22.5 9.53333C24.9581 9.53333 26.9563 11.3867 26.9563 13.6667V16.3333Z"/></svg>
      </div>
    </div>
  </div>
</template>

<script>
import EpisodeService from '@/services/Episode';
import SegmentEvent from '@/services/PlayerEvent';
import AudioIcon from '@/components/webv2/shared/AudioIcon';
import ListenFreeTag from '@/components/webv2/shared/ListenFreeTag';

import showDetailsHelper from '@/components/webv2/showDetails/showDetailsHelper';

export default {
  components: {
    AudioIcon,
    ListenFreeTag,
  },
  props: {
    episode: {
      type: [Object, Boolean],
      default() {
        return false;
      },
    },
  },
  data() {
    return {
      episode_service: new EpisodeService(),
      eventProvider: new SegmentEvent(),
    };
  },
  computed: {
    userPremium() {
      return this.userSubscriptionState === 'premium' || this.userSubscriptionState === 'premiumChurned';
    },
    userNotPremium() {
      return this.userSubscriptionState === 'anon' || this.userSubscriptionState === 'free' || this.userSubscriptionState === 'freeWasPremium';
    },
    userNotAnon() {
      return !(this.userSubscriptionState === 'anon');
    },
    showBookmarkCta() {
      return this.userPremium || (this.userNotAnon && !this.episode.isExclusive);
    },
    isListenAvailable() {
      return this.episode && (this.userPremium || this.episode.isFree);
    },
    showSubscribeCta() {
      return this.episode && this.episode.isExclusive && this.userNotPremium;
    },
    episodeDetails() {
      if (!this.episode) return '';
      const episodeDetailsArray = [];
      if (this.episode.season && this.episode.number) {
        const seasonText = `S${this.episode.season} E${this.episode.number}`;
        episodeDetailsArray.push(seasonText);
      }
      if (this.episode.releasedAt) {
        const date = this.$options.filters.dateFormat(this.episode.releasedAt * 1000);
        episodeDetailsArray.push(date);
      }
      if (this.episode.runtime) {
        const runtime = this.$options.filters.humanizedTimeFilter(this.episode.runtime);
        episodeDetailsArray.push(runtime);
      }
      return episodeDetailsArray.join(' | ');
    },
    episodeSlugLink() {
      if (!this.episode) return '';
      const publisherSlug = this.convertToSlug(this.episode.publisherName);
      const podcastSlug = this.convertToSlug(this.episode.showTitle);
      const episodeSlug = this.convertToSlug(this.episode.title);
      const episodeId = this.episode.id;

      return `/listen/${publisherSlug}/${podcastSlug}/${episodeSlug}/${episodeId}`;
    },
    hasEpisodeImage() {
      return this.episode && (this.episode.images.thumbnail || this.episode.images.default);
    },
    episodeImage() {
      if (!this.episode) return '';
      if (this.episode.images.thumbnail) {
        return this.episode.images.thumbnail;
      }
      if (this.episode.images.default) {
        return this.episode.images.default;
      }
      return '';
    },
    episodeName() {
      return this.episode && this.episode.title;
    },
    episodeDescription() {
      return this.episode && this.episode.description;
    },
    accessText() {
      if (!this.episode) return '';
      if (this.episode.isFree) return 'Free Episode';
      return this.userPremium ? '' : 'Subscribe to Listen';
    },
  },
  methods: {
    toggleCardOpen() {
      this.isCardOpen = !this.isCardOpen;
    },
    formatLegacyEpisodeObject(episode) {
      return showDetailsHelper.formatLegacyEpisodeObject(episode);
    },
    toSubscribe() {
      const { isFreeUser, isPremiumUser } = this.promoMeta;
      if (isPremiumUser) return;
      const routeTo = { path: '/signup' };
      if (isFreeUser) {
        routeTo.path = '/plans';
      }
      this.$analytics.track('Locked Episode Click', {
        screen_name: this.$route.name,
        path: this.$route.path,
        btnId: this.episode.id,
      });
      this.$router.push(routeTo);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/variables.scss";

.episode-item {
  padding: 10px 0;
  margin-left: 10px;
  width: 580px;
  min-width: 350px;
  display: flex;
  flex-flow: row nowrap;
  border-right: 1px solid #272F41;
  max-width: 50%;
  &:nth-child(2n) {
    border-right: 0;
  }
  @media(max-width: 1190px) {
    max-width: 100%;
    min-width: 60%;
    border-right: 0;
    border-bottom: 1px solid #272F41;
  }
  &__thumb {
    flex: 0 0 90px;
    img {
      width: 100%;
    }
    @media (max-width: $xs) {
      flex: 0 0 65px;
    }
  }
  &__details {
    flex: 1 1 auto;
    padding: 0 18px;
    text-align: left;
    align-self: center;
  }
  &__access-text {
    font-family: $patron-bold;
    color: $peach;
    font-size: 12px;
    line-height: 13px;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-top: 0;
    margin-bottom: 6px;
  }
  &__name {
    font-family: $patron-bold;
    color: $white;
    font-size: 18px;
    line-height: 24px;
    margin-top: 0;
    margin-bottom: 6px;
    @media(min-width: $sm) {
      font-size: 24px;
      margin-bottom: 10px;
    }

    a {
      color: $white;
      &:hover {
        color: $blue;
        text-decoration: underline;
      }
    }
  }
  &__description {
    font-family: $patron;
    color: $light-gray3;
    font-size: 14px;
    line-height: 20px;
    margin-top: 0;
    margin-bottom: 0;
    @media(min-width: $sm) {
      font-size: 16px;
    }
  }
  &__actions {
    flex: 0 0 90px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__action-locked {
    cursor: pointer;
    width: 42px;
    height: 42px;
    svg {
      width: 100%;
    }
  }
}

.loading-placeholder {
  $from: #343B4A;
  $to: scale-color($from, $lightness: -10%);
  background: linear-gradient(
      -90deg,
      #2B3345 0%,
      #2E3950 50%,
      #2B3345 100%
  );
  background-size: 400% 400%;
  animation: pulse 2s ease-in-out infinite;
  @keyframes pulse {
    0% {
      background-position: 0% 0%;
    }
    100% {
      background-position: -135% 0%;
    }
  }
  &.string {
    height: 1em;
  }
  &.square {
    width: 90%;
    padding-top: 90%;
  }
}
</style>
